import React, { useEffect, useMemo, useState } from "react";
import api from "../services/api.js";
import Pagination from "../components/Customer/Pagination.js";
import AddTransactionModal from "../components/Transaction/AddTransactionModal.js";
import AddBulkTransactionModal from "../components/Transaction/AddBulkTransactionModal.js";
import TransactionsChart from "../components/charts/transactionsChart.js";

const MAX_PAGE = 100;

export default function Transactions({ role }) {
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [openBulkModal, setOpenBulkModal] = useState(false);

  const [exporting, setExporting] = useState(false);

  // ================= FILTER STATE (BACKEND PARAMS) =================
  const [filters, setFilters] = useState({
    userid: "",
    customerId: "",
    customerName: "",
    invoiceid: "",
    gateway: "",
    transid: "",
    date_from: "",
    date_to: "",
    sortBy: "date",
    sortOrder: "DESC",
    limit: 25,
  });

  // ================= QUERY PARAMS =================
  const [params, setParams] = useState({
    page: 1,
    ...filters,
  });

  // ================= GLOBAL SEARCH =================
  const [globalSearch, setGlobalSearch] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/transaction-all", {
        params,
        headers: { Authorization: token },
      });

      setRows(res.data.data || []);
      setSummary(res.data.summary || null);
      setPagination(res.data.pagination || {});
      setChartData(res.data.data_chart || []);
    } catch (err) {
      console.error("Fetch transactions failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // ================= SEARCH HANDLER =================
  const handleSearch = () => {
    setParams({
      page: 1,
      ...filters,
    });
  };

  const handleReset = () => {
    const reset = {
      userid: "",
      customerId: "",
      customerName: "",
      invoiceid: "",
      gateway: "",
      transid: "",
      date_from: "",
      date_to: "",
      sortBy: "date",
      sortOrder: "DESC",
      limit: 25,
    };

    setFilters(reset);
    setParams({ page: 1, ...reset });
    setGlobalSearch("");
  };

  // ================= CLIENT SIDE GLOBAL SEARCH =================
  const filteredRows = useMemo(() => {
    if (!globalSearch) return rows;

    const keyword = globalSearch.toLowerCase();

    return rows.filter((row) =>
      [
        row.userid,
        row.customer_id,
        row.full_name,
        row.invoiceid,
        row.transid,
        row.gateway,
      ]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );
  }, [rows, globalSearch]);

  const totalPages = Math.min(pagination.total_pages || 1, MAX_PAGE);

  const formatDate = (val) => {
    if (!val) return "-";
    return new Date(val).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.page,
    params.limit,
    params.userid,
    params.invoiceid,
    params.gateway,
    params.transid,
    params.date_from,
    params.date_to,
    params.sortBy,
    params.sortOrder,
  ]);

  const handleDeleteTransaction = async (transactionId) => {
    if (!transactionId) return;

    const confirmDelete = window.confirm(
      "Yakin ingin menghapus transaksi ini?\nInvoice terkait akan dikembalikan ke status Unpaid."
    );

    if (!confirmDelete) return;

    try {
      setDeletingId(transactionId);

      await api.delete(`/delete-transaction/${transactionId}`, {
        headers: {
          Authorization: token,
        },
      });

      alert("Transaksi berhasil dihapus");

      // refresh data
      fetchData();
    } catch (err) {
      console.error("Delete transaction failed:", err);
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Gagal menghapus transaksi"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleSuccess = (data) => {
    console.log("Bulk transaction result:", data);
    // optional: refresh table / fetch ulang data transaksi
  };

  const exportAllTransactionsCSV = async () => {
    try {
      setExporting(true);

      let allRows = [];
      let page = 1;
      let totalPages = 1;

      do {
        const res = await api.get("/transaction-all", {
          params: {
            ...filters,
            page,
            limit: 100,
          },
          headers: {
            Authorization: token,
          },
        });

        const dataRows = res.data.data || [];
        const paginationInfo = res.data.pagination || {};

        allRows = allRows.concat(dataRows);
        totalPages = Math.min(paginationInfo.total_pages || 1, MAX_PAGE);
        page++;
      } while (page <= totalPages);

      if (!allRows.length) return;

      // ================= CSV HEADER =================
      const headers = [
        "Transaction ID",
        "User ID",
        "Customer ID",
        "Customer Name",
        "Gateway",
        "Payment Method",
        "Transaction Date",
        "Amount In",
        "Invoice ID",
        "Date Paid",
      ];

      // ================= CSV ROWS =================
      const rows = allRows.map((row) => [
        row.id,
        row.userid,
        row.customer_id,
        row.full_name,
        row.gateway ?? "-",
        row.paymentmethod ?? "-",
        formatDate(row.date),
        row.amountin,
        row.invoiceid ?? "-",
        formatDate(row.datepaid),
      ]);

      const csv = [
        headers.join(","),
        ...rows.map((r) => r.map((v) => JSON.stringify(v ?? "")).join(",")),
      ].join("\n");

      const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `transactions-all-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;

      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export Transactions CSV failed:", err);
      alert("Gagal export transaksi");
    } finally {
      setExporting(false);
    }
  };

  const formattedChartData = useMemo(() => {
    return chartData.map((item) => ({
      ...item,
      name: new Date(item.date).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
      }),
    }));
  }, [chartData]);

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
        Transactions
      </h2>

      {/* CHART */}
      <div className="p-4 rounded-xl bg-white dark:bg-gray-800 shadow">
        <h3 className="text-lg font-semibold mb-4">Transactions Overview</h3>

        <TransactionsChart data={formattedChartData} />
      </div>

      {/* SUMMARY */}
      {summary && ["billing", "admin"].includes(role) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ResumeCard
            label="Total Transaksi"
            value={summary.total_transaction}
          />
          <ResumeCard label="Amount In" value={summary.total_amountin} />
          <ResumeCard label="Amount Out" value={summary.total_amountout} />
          <ResumeCard label="Fees" value={summary.total_fees} />
        </div>
      )}

      {/* ================= FILTER ================= */}
      <div className="grid md:grid-cols-4 gap-3">
        <input
          placeholder="User ID"
          value={filters.userid}
          onChange={(e) => setFilters({ ...filters, userid: e.target.value })}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          placeholder="Customer ID"
          value={filters.customerId}
          onChange={(e) =>
            setFilters({ ...filters, customerId: e.target.value })
          }
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          placeholder="Customer Name"
          value={filters.customerName}
          onChange={(e) =>
            setFilters({ ...filters, customerName: e.target.value })
          }
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          placeholder="Invoice ID"
          value={filters.invoiceid}
          onChange={(e) =>
            setFilters({ ...filters, invoiceid: e.target.value })
          }
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          placeholder="Transaction ID"
          value={filters.transid}
          onChange={(e) => setFilters({ ...filters, transid: e.target.value })}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <select
          value={filters.gateway}
          onChange={(e) => setFilters({ ...filters, gateway: e.target.value })}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">Any Gateway</option>
          <option value="SKINET">SKINET</option>
          <option value="DUITKU">DUITKU</option>
        </select>

        <input
          type="date"
          value={filters.date_from}
          onChange={(e) =>
            setFilters({ ...filters, date_from: e.target.value })
          }
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />
      </div>

      {/* ================= ACTION BUTTON ================= */}
      <div className="flex gap-3">
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Search
        </button>

        <button
          onClick={handleReset}
          className="px-4 py-2 bg-gray-500 text-white rounded"
        >
          Reset
        </button>
      </div>

      {/* ================= GLOBAL SEARCH & ACTION ================= */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        {/* Global Search - Left */}
        <input
          type="text"
          placeholder="Quick Search"
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          className="w-full md:w-72 px-3 py-2 rounded border
      bg-white text-gray-800
      dark:bg-slate-800 dark:text-white dark:border-slate-700
      focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* ================= ACTION ================= */}
      <div className="flex justify-end gap-2">
        <button
          onClick={exportAllTransactionsCSV}
          disabled={exporting}
          className="px-4 py-2 rounded bg-green-600 text-white
      hover:bg-green-700 transition disabled:opacity-60"
        >
          {exporting ? "Exporting..." : "Export ALL CSV"}
        </button>

        {role === "billing" && (
          <>
            <button
              onClick={() => setOpenAddModal(true)}
              className="px-4 py-2 rounded bg-blue-600 text-white
          hover:bg-blue-700 transition"
            >
              Single Transaction
            </button>

            <button
              onClick={() => setOpenBulkModal(true)}
              className="px-4 py-2 rounded bg-blue-600 text-white
          hover:bg-blue-700 transition"
            >
              Bulk Transaction
            </button>
          </>
        )}

        {/* Modals (tidak mempengaruhi layout) */}
        <AddTransactionModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          token={token}
          onSuccess={fetchData}
        />

        <AddBulkTransactionModal
          open={openBulkModal}
          onClose={() => setOpenBulkModal(false)}
          token={token}
          onSuccess={handleSuccess}
        />
      </div>

      {/* Transaction table di sini */}
      {/* TABLE */}
      <div className="overflow-x-auto rounded-xl shadow bg-white dark:bg-gray-800">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr className="text-left">
              <Th>ID</Th>
              <Th>User</Th>
              <Th>Customer ID</Th>
              <Th>Customer Name</Th>
              <Th>Gateway</Th>
              <Th>Payment Method</Th>
              <Th>Date</Th>
              <Th>Amount In</Th>
              <Th>Invoice</Th>
              <Th>Date Paid</Th>
              {role === "billing" && <Th className="text-center">Action</Th>}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="p-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan="11" className="p-4 text-center">
                  No data
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.id} className="border-t">
                  <Td>{row.id}</Td>
                  <Td>
                    <a
                      href={`/customer-detail/${row.userid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.userid}
                    </a>
                  </Td>
                  <Td>{row.customer_id}</Td>
                  <Td>{row.full_name}</Td>
                  <Td>{row.gateway}</Td>
                  <Td>{row.paymentmethod}</Td>
                  <Td>{formatDate(row.date)}</Td>
                  <Td>{row.amountin}</Td>
                  <Td>
                    <a
                      href={`/invoice-detail/${row.invoiceid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.invoiceid || "-"}
                    </a>
                  </Td>
                  <Td>{formatDate(row.datepaid)}</Td>
                  {role === "billing" && (
                    <Td className="text-center">
                      <button
                        onClick={() => handleDeleteTransaction(row.id)}
                        disabled={deletingId === row.id}
                        className={`px-3 py-1 text-xs rounded transition
          ${
            deletingId === row.id
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-red-600 hover:bg-red-700 text-white"
          }
        `}
                      >
                        {deletingId === row.id ? "Deleting..." : "Delete"}
                      </button>
                    </Td>
                  )}
                </tr>
              ))
            )}
          </tbody>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="p-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-4 text-center">
                  No data
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <Td>{row.id}</Td>
                  <Td>
                    <a
                      href={`/customer-detail/${row.userid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.userid}
                    </a>
                  </Td>
                  <Td>{row.customer_id}</Td>
                  <Td>{row.full_name}</Td>
                  <Td>{row.gateway || "-"}</Td>
                  <Td>{row.paymentmethod || "-"}</Td>
                  <Td>{formatDate(row.date)}</Td>
                  <Td>{row.amountin}</Td>
                  <Td>
                    <a
                      href={`/invoice-detail/${row.invoiceid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.invoiceid || "-"}
                    </a>
                  </Td>
                  <Td>{formatDate(row.datepaid)}</Td>
                  {role === "billing" && (
                    <Td className="text-center">
                      <button
                        onClick={() => handleDeleteTransaction(row.id)}
                        disabled={deletingId === row.id}
                        className={`px-3 py-1 text-xs rounded transition
          ${
            deletingId === row.id
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-red-600 hover:bg-red-700 text-white"
          }
        `}
                      >
                        {deletingId === row.id ? "Deleting..." : "Delete"}
                      </button>
                    </Td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={totalPages}
          onPageChange={(page) => setParams((prev) => ({ ...prev, page }))}
        />
      )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

const ResumeCard = ({ label, value }) => (
  <div className="p-4 rounded-xl bg-white dark:bg-gray-800 shadow">
    <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    <div className="text-xl font-semibold">{value}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="px-4 py-3 font-semibold">{children}</th>
);

const Td = ({ children }) => <td className="px-4 py-2">{children}</td>;
